import styled from 'styled-components';

import { Translate } from '@dreryk/react-common';
import {
   ButtonVariant,
   fonts,
   LoaderSpinner,
   ModalCancelButton,
   ModalCloseButton,
   ModalConfirmButton,
   useHistoryPushWithBackAction,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { getHealthcareNfzSettingsPath } from '~legacy.p1';
import { useGetCurrentHealthcareProviderType } from '~libs.shared.account';

import { medicalDevicesTranslationKeys } from '../translations';

import { MedicalDeviceAllFieldsFormController } from './medicalDeviceOrder.types';
import { MedicalDeviceOrderModalPrintButton } from './MedicalDeviceOrderModalPrintButton';

type MedicalDeviceOrderErrorsModalContentProps = Pick<MedicalDeviceAllFieldsFormController, 'handleSubmit'> & {
   readonly onCancel: VoidFunction;
   readonly onClose: VoidFunction;
   readonly onPrintSuccess: VoidFunction;
   readonly printableWithoutVerification: boolean;
};

const StyledIdentifiedErrors = styled(Translate)`
   font-weight: ${fonts.fontWeightBold};
`;
StyledIdentifiedErrors.displayName = 'StyledIdentifiedErrors';

export const MedicalDeviceOrderNoNfzConfigurationModalContent = (
   props: MedicalDeviceOrderErrorsModalContentProps
): JSX.Element => {
   const healthcareProviderType = useGetCurrentHealthcareProviderType();
   const updatingProviderDataAvailable = protectFeature('updatingProviderData');
   const push = useHistoryPushWithBackAction(true);

   if (!healthcareProviderType) {
      return <LoaderSpinner />;
   }

   const goToNfzSettings = (): void => {
      push(getHealthcareNfzSettingsPath(healthcareProviderType), {
         label: medicalDevicesTranslationKeys.goBackToVisit,
      });
   };

   const shouldDisplayConfirmButton = !updatingProviderDataAvailable && !props.printableWithoutVerification;

   return (
      <>
         <Translate>
            {protectFeature(
               'updatingProviderData',
               medicalDevicesTranslationKeys.nfzConfigurationMissingModalTextContentForAdmin,
               medicalDevicesTranslationKeys.nfzConfigurationMissingModalTextContentForNonAdmin
            )}
         </Translate>
         <ModalCloseButton onClick={props.onClose} />
         {protectFeature(
            'updatingProviderData',
            <ModalConfirmButton
               variant={props.printableWithoutVerification ? ButtonVariant.SECONDARY : ButtonVariant.PRIMARY}
               onClick={goToNfzSettings}
               label={medicalDevicesTranslationKeys.goToNfzSettings}
            />,
            null
         )}
         {props.printableWithoutVerification ? (
            <MedicalDeviceOrderModalPrintButton
               handleSubmit={props.handleSubmit}
               onPrintSuccess={props.onPrintSuccess}
            />
         ) : null}
         {shouldDisplayConfirmButton ? (
            <ModalConfirmButton
               variant={ButtonVariant.PRIMARY}
               onClick={props.onClose}
               label={medicalDevicesTranslationKeys.ok}
            />
         ) : null}
         {!shouldDisplayConfirmButton && <ModalCancelButton onClick={props.onCancel} />}
      </>
   );
};
